/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  GEOWEB_ROLE_PRESETS_ADMIN,
  getQueryClient,
  useAuthenticationContext,
} from '@opengeoweb/api';
import {
  defaultFormOptions,
  ReactHookFormProvider,
} from '@opengeoweb/form-fields';
import {
  ConfirmationButtons,
  CustomDialog,
  getAxiosErrorMessage,
  isAxiosError,
} from '@opengeoweb/shared';
import ViewPresetsForm from './ViewPresetsForm';
import {
  useSaveViewPreset,
  useSaveViewPresetAs,
} from '../../api/viewpreset-api/hooks';
import { viewpresetQueryOptions } from '../../api/viewpreset-api/queryOptions';
import type {
  ViewPreset,
  ViewPresetFormValues,
  WorkspaceSupportedComponentTypes,
} from '../../store/viewPresets/types';
import { PresetAction } from '../../store/viewPresets/types';
import type { WorkspacePresetLabels } from '../../store/workspace/types';
import { useWorkspaceTranslation } from '../../utils/i18n';

export interface ViewPresetsFormDialogProps {
  isOpen: boolean;
  title: string;
  action: PresetAction;
  viewPresetId: string;
  viewComponentType: WorkspaceSupportedComponentTypes;
  formValues?: ViewPresetFormValues;
  onSuccess: (
    action: PresetAction,
    presetId: string,
    title: string,
    description: string,
    abstract: string,
    labels: WorkspacePresetLabels,
  ) => void;
  onClose: () => void;
  onReset: (viewPresetId: string) => void;
  onDelete: (variables: { id: string }) => Promise<Headers | null>;
}

const ViewPresetsFormDialog: React.FC<ViewPresetsFormDialogProps> = ({
  isOpen,
  action,
  viewPresetId,
  viewComponentType,
  formValues,
  onSuccess,
  onClose,
  title,
  onReset,
  onDelete,
}: ViewPresetsFormDialogProps) => {
  const { handleSubmit: handleFormSubmit, reset } =
    useFormContext<ViewPreset>();
  const { t } = useWorkspaceTranslation();
  const { currentRole, auth } = useAuthenticationContext();
  const { mutateAsync: saveViewPreset } = useSaveViewPreset();
  const { mutateAsync: saveViewPresetAs } = useSaveViewPresetAs();
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;
  const [error, setError] = React.useState<string>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!isOpen) {
      setError(null!);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (formValues) {
      reset(formValues);
    }
  }, [formValues, reset]);

  const getConfirmLabel = (action: PresetAction): string => {
    switch (action) {
      case PresetAction.DELETE: {
        return t('workspace-delete');
      }
      case PresetAction.SAVE_AS: {
        return t('workspace-save');
      }
      case PresetAction.RESET: {
        return t('workspace-reset');
      }
      default:
        return t('workspace-save');
    }
  };

  const confirmLabel = getConfirmLabel(action);

  const onFormSubmit = async (formValues: ViewPreset): Promise<void> => {
    const scope = isAdmin ? 'system' : formValues.scope;
    const componentType =
      formValues.componentType || viewComponentType || ('Map' as const);

    const handleSuccess = (id: string): void => {
      setIsLoading(false);
      onClose();
      onSuccess(
        action,
        id,
        newFormValues.title,
        newFormValues.description!,
        newFormValues.abstract!,
        newFormValues.labels!,
      );
    };

    const newFormValues = {
      ...formValues,
      scope,
      componentType,
    };
    try {
      setError(null!);
      setIsLoading(true);

      switch (action) {
        case PresetAction.SAVE_AS: {
          const newId = await saveViewPresetAs({ data: newFormValues });
          handleSuccess(newId);
          break;
        }
        case PresetAction.DELETE: {
          await onDelete({ id: viewPresetId });
          handleSuccess(viewPresetId);
          break;
        }
        case PresetAction.EDIT: {
          // Retrieve stored preset so we ensure only to update the title
          const originalViewPreset = await getQueryClient().fetchQuery(
            viewpresetQueryOptions.getViewPreset(viewPresetId, auth!),
          );
          const viewPresetEdited: ViewPreset = {
            ...originalViewPreset,
            title: newFormValues.title,
            description: newFormValues.description,
            abstract: newFormValues.abstract,
            labels: newFormValues.labels,
            scope,
          };
          await saveViewPreset({ id: viewPresetId, data: viewPresetEdited });
          handleSuccess(viewPresetId);

          break;
        }
        case PresetAction.RESET: {
          onReset(viewPresetId);
          handleSuccess(viewPresetId);
          break;
        }
        default: {
          break;
        }
      }
    } catch (error) {
      setIsLoading(false);
      const submitError = error as Error;
      const message = isAxiosError(submitError)
        ? getAxiosErrorMessage(submitError)
        : submitError.message;
      setError(message);
    }
  };

  const onSubmit = async (): Promise<void> => {
    await handleFormSubmit(async (newFormValues: ViewPreset): Promise<void> => {
      const { title, ...otherFormValues } = newFormValues;
      const trimmedTitle = title?.trim();

      const parsedFormValues = {
        ...otherFormValues,
        title: trimmedTitle,
      };

      await onFormSubmit(parsedFormValues);
    })();
  };

  const withLabelsPanel =
    action === PresetAction.SAVE_AS || action === PresetAction.EDIT;

  return (
    <CustomDialog
      actions={
        <ConfirmationButtons
          cancelLabel={t('workspace-cancel')}
          confirmLabel={confirmLabel}
          isLoading={isLoading}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      }
      data-testid="map-preset-dialog"
      description=""
      headerActions={withLabelsPanel ? [] : undefined}
      isResizable
      key={withLabelsPanel ? 'wide' : 'narrow'}
      minWidth={withLabelsPanel ? 635 : undefined}
      onClose={onClose}
      onSubmit={onSubmit}
      open={isOpen}
      resizableDefaultSize={{
        width: withLabelsPanel ? 700 : 330,
        height: withLabelsPanel ? 650 : 'auto',
      }}
      sx={
        withLabelsPanel
          ? {
              '& [data-testid="dialog-action-header"]': {
                display: 'none',
              },
              '& .MuiDialogContent-root': {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              },
            }
          : undefined
      }
      title={withLabelsPanel ? undefined : title}
    >
      <ViewPresetsForm
        action={action}
        cancelLabel={t('workspace-cancel')}
        confirmLabel={confirmLabel}
        dialogTitle={title}
        error={error}
        formValues={formValues!}
        isLoading={isLoading}
        onClose={onClose}
        onSubmit={onSubmit}
        showInlineActions={withLabelsPanel}
        viewComponentType={viewComponentType}
      />
    </CustomDialog>
  );
};

const ViewPresetsFormDialogWrapper: React.FC<ViewPresetsFormDialogProps> = ({
  ...props
}: ViewPresetsFormDialogProps) => {
  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
      }}
    >
      <ViewPresetsFormDialog {...props} />
    </ReactHookFormProvider>
  );
};

export default ViewPresetsFormDialogWrapper;
